// Question — a quiz question as an object among the text (pen "Always
// writing", piece 6): a question tile, "Question n" and its meta, the stem
// in the reading face, then each answer behind a letter tile with its why
// line hanging under it. The right answer's tile is green with a check and
// "Right answer"; clicking another letter makes that one right (a question
// always has exactly one). The question being written in shows its edge.
//
// Typed with plain keys (questionKeys.ts): Enter goes stem, answer, why,
// next answer; Enter on an empty answer ends the question with an ordinary
// line below it; Backspace on an empty line goes back up. No "Add answer"
// button, no chords.
//
// Projection + intents: the question comes in as props and every change
// goes out through the host's handlers. The one piece of local state is
// which line the caret goes to next (view-ephemeral: it does not survive a
// reload and means nothing to the domain). A line it points at mounts
// afresh with the caret in it (its key carries the move's count).

export { Question };

import * as Limits from "@bilda/core/limits";
import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { SegmentedControl } from "../../SegmentedControl/SegmentedControl.js";
import { EditableText } from "../EditableText/EditableText.js";
import { InlineDiff } from "../InlineDiff/InlineDiff.js";
import {
    type QuestionKey,
    type QuestionLine,
    type QuestionStep,
    questionStep,
} from "../questionKeys.js";
import type {
    Choice,
    DiffOp,
    ElementRendererProps,
    McqElement,
} from "../types.js";
import "./Question.css";

// Where the caret goes next, and how many moves there have been (a line it
// points at again mounts afresh).
type Caret = { readonly line: QuestionLine; readonly move: number };

const sameLine = (a: QuestionLine, b: QuestionLine): boolean =>
    a.type === b.type &&
    (a.type === "stem" || (b.type !== "stem" && a.index === b.index));

// A, B, C … by the answer's place.
const letterOf = (index: number): string => String.fromCharCode(65 + index);

const Question = ({
    element,
    edit,
    ordinal,
    onEditText,
    onDraftText,
    onEditFeedback,
    onDraftFeedback,
    onMarkChoiceCorrect,
    onUnmarkChoiceCorrect,
    onAddChoice,
    onEndQuestion,
    onRemoveChoice,
    onMoveChoice,
    onAllowSingleAnswer,
    onAllowMultipleAnswers,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<McqElement> & {
    // A field-grain proposal to draw in place of the stem.
    readonly edit: DiffOp | undefined;
}): ReactNode => {
    const { t } = useLingui();
    const [caret, setCaret] = useState<Caret | undefined>(undefined);
    const go = (line: QuestionLine) =>
        setCaret((c) => ({ line, move: (c?.move ?? 0) + 1 }));
    const isAt = (line: QuestionLine): boolean =>
        caret !== undefined && sameLine(caret.line, line);
    const keyOf = (base: string, line: QuestionLine): string =>
        caret !== undefined && isAt(line) ? `${base}:${caret.move}` : base;

    const id = element.id;
    const max = Limits.QUESTION_ANSWERS_MAX;
    const single = element.answerMode === "single";
    const full = element.choices.length >= max;

    // A key in a line: the step it takes, with the line's text kept first
    // (unless the step carries it itself).
    const onKey = (
        at: QuestionLine,
        key: QuestionKey,
        keep: (text: string) => void,
    ) => {
        const text = key.type === "enter" ? key.text : "";
        const step: QuestionStep = questionStep(element, max, at, key);
        switch (step.type) {
            case "end-question": {
                const answer =
                    at.type === "answer"
                        ? element.choices[at.index]
                        : undefined;
                if (answer === undefined) return;
                onEndQuestion?.(
                    id,
                    step.removes === undefined
                        ? { type: "keep-answer", choiceId: answer.id, text }
                        : { type: "drop-answer", choiceId: step.removes },
                );
                return;
            }
            case "add-answer": {
                const why =
                    at.type === "why" ? element.choices[at.index] : undefined;
                onAddChoice?.(
                    id,
                    why === undefined
                        ? { type: "stem", text }
                        : { type: "why", choiceId: why.id, feedback: text },
                );
                go(step.to);
                return;
            }
            case "remove-answer":
                onRemoveChoice?.(id, step.choiceId);
                go(step.to);
                return;
            case "caret":
                keep(text);
                go(step.to);
                return;
            // At the ceiling, or nothing to do: the text is kept and the
            // caret stays in the line.
            case "full":
            case "stay":
                keep(text);
                go(at);
                return;
            default:
                return exhaustSwitchCase(step);
        }
    };

    // One line of the question, written in place (read-only: its text).
    const line = (props: {
        readonly at: QuestionLine;
        readonly name: string;
        readonly value: string;
        readonly label: string;
        readonly placeholder: string;
        readonly className: string;
        readonly keep: ((text: string) => void) | undefined;
        readonly draft: ((text: string) => void) | undefined;
        readonly backspace: boolean;
        readonly first?: boolean;
    }): ReactNode => {
        const { at, keep } = props;
        if (keep === undefined)
            return props.value === "" ? null : (
                <span className={props.className}>{props.value}</span>
            );
        return (
            <EditableText
                key={keyOf(props.name, at)}
                className={props.className}
                value={props.value}
                ariaLabel={props.label}
                placeholder={props.placeholder}
                maxLength={Limits.BLOCK_TEXT_MAX}
                onCommit={keep}
                onDraft={props.draft}
                onEnterAtEnd={(text) =>
                    onKey(at, { type: "enter", text }, keep)
                }
                onBackspaceEmpty={
                    props.backspace
                        ? () => onKey(at, { type: "backspace-empty" }, keep)
                        : undefined
                }
                // The host's signal (a question just made) for the stem, or
                // this question's own caret.
                autoEdit={isAt(at) || (props.first === true && autoEdit)}
                onAutoEditConsumed={
                    props.first === true ? onAutoEditConsumed : undefined
                }
            />
        );
    };

    const answerRow = (choice: Choice, i: number): ReactNode => {
        const letter = letterOf(i);
        const makeRight =
            onMarkChoiceCorrect === undefined
                ? undefined
                : () =>
                      choice.correct
                          ? single
                              ? undefined
                              : onUnmarkChoiceCorrect?.(choice.id)
                          : onMarkChoiceCorrect(choice.id);
        const editable = onEditText !== undefined;
        return (
            <li key={choice.id} className="bilda-question__answer">
                <div className="bilda-question__answer-row">
                    {makeRight === undefined ? (
                        <span
                            className="bilda-question__letter"
                            data-right={choice.correct || undefined}
                        >
                            {letter}
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="bilda-question__letter"
                            data-right={choice.correct || undefined}
                            aria-pressed={choice.correct}
                            aria-label={t`Make answer ${letter} the right one`}
                            onClick={makeRight}
                        >
                            {letter}
                        </button>
                    )}
                    <div className="bilda-question__answer-text">
                        {line({
                            at: { type: "answer", index: i },
                            name: `${choice.id}:answer`,
                            value: choice.text,
                            label: t`Answer ${letter}`,
                            placeholder: t({
                                message: "Answer",
                                context: "answer placeholder",
                            }),
                            className: "bilda-question__answer-field",
                            keep:
                                onEditText &&
                                ((text) => onEditText(choice.id, text)),
                            draft:
                                onDraftText &&
                                ((text) => onDraftText(choice.id, text)),
                            backspace: true,
                        })}
                        {choice.correct ? (
                            <span className="bilda-question__right">
                                <Icon name="check" size="xs" />
                                {t`Right answer`}
                            </span>
                        ) : null}
                    </div>
                    {editable &&
                    (onMoveChoice !== undefined ||
                        onRemoveChoice !== undefined) ? (
                        // Reorder and remove, quiet until the answer is
                        // pointed at or written in.
                        <div className="bilda-question__controls">
                            {onMoveChoice !== undefined && i > 0 ? (
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    label={t`Move answer up`}
                                    onClick={() =>
                                        onMoveChoice(id, choice.id, i - 1)
                                    }
                                >
                                    <Icon name="chevron-up" size="sm" />
                                </IconButton>
                            ) : null}
                            {onMoveChoice !== undefined &&
                            i < element.choices.length - 1 ? (
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    label={t`Move answer down`}
                                    onClick={() =>
                                        onMoveChoice(id, choice.id, i + 1)
                                    }
                                >
                                    <Icon name="chevron-down" size="sm" />
                                </IconButton>
                            ) : null}
                            {onRemoveChoice !== undefined &&
                            element.choices.length > 1 ? (
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    label={t`Remove answer`}
                                    onClick={() =>
                                        onRemoveChoice(id, choice.id)
                                    }
                                >
                                    <Icon name="trash" size="sm" />
                                </IconButton>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                {line({
                    at: { type: "why", index: i },
                    name: `${choice.id}:why`,
                    value: choice.feedback ?? "",
                    label: t`Why, for answer ${letter}`,
                    placeholder: choice.correct
                        ? t`Why it is right, shown after answering`
                        : t`Why it is wrong, shown after answering`,
                    className: "bilda-question__why",
                    keep:
                        onEditFeedback &&
                        ((text) => onEditFeedback(choice.id, text)),
                    draft:
                        onDraftFeedback &&
                        ((text) => onDraftFeedback(choice.id, text)),
                    backspace: true,
                })}
            </li>
        );
    };

    return (
        <div className="bilda-question">
            <div className="bilda-question__head">
                <span className="bilda-question__tile">
                    <Icon name="question" size="sm" />
                </span>
                <span className="bilda-question__name">
                    {ordinal === undefined
                        ? t`Question`
                        : t`Question ${ordinal}`}
                </span>
                <span className="bilda-question__meta">
                    {single ? t`One right answer` : t`Multiple answers`}
                </span>
                {onAllowSingleAnswer !== undefined &&
                onAllowMultipleAnswers !== undefined ? (
                    // The answer mode, quiet until the question is pointed
                    // at or written in. Each mode is its own command.
                    <div className="bilda-question__mode">
                        <SegmentedControl
                            size="sm"
                            ariaLabel={t`Answer mode`}
                            value={element.answerMode}
                            options={[
                                { value: "single", label: t`Single answer` },
                                {
                                    value: "multiple",
                                    label: t`Multiple answers`,
                                },
                            ]}
                            onChange={(v) =>
                                v === "multiple"
                                    ? onAllowMultipleAnswers(id)
                                    : onAllowSingleAnswer(id)
                            }
                        />
                    </div>
                ) : null}
            </div>
            <div className="bilda-question__stem">
                {edit !== undefined ? (
                    <InlineDiff op={edit} />
                ) : (
                    line({
                        at: { type: "stem" },
                        name: "stem",
                        value: element.stem,
                        label: t`Question`,
                        placeholder: t`Question`,
                        className: "bilda-question__stem-field",
                        keep: onEditText && ((text) => onEditText(id, text)),
                        draft: onDraftText && ((text) => onDraftText(id, text)),
                        backspace: false,
                        first: true,
                    })
                )}
            </div>
            <ol className="bilda-question__answers">
                {element.choices.map(answerRow)}
            </ol>
            {onEditText === undefined ? null : full ? (
                <p className="bilda-question__limit" role="status">
                    {t`A question holds up to ${max} answers`}
                </p>
            ) : (
                <p className="bilda-question__hint">
                    {t`Enter goes answer, why, next answer · Enter on an empty answer ends the question · click a letter to make it the right answer`}
                </p>
            )}
        </div>
    );
};
